<template>
  <div id="validatorsComponent">
    <page-title
      :heading="heading"
      :subheading="subheading"
      :icon="icon"
    ></page-title>
    <v-container>
      <v-layout class="mb-2" row wrap>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="validatorSearch"
          :label="$t('general.search')"
          clearable
          append-outer-icon="search"
        >
        </v-text-field>
        <v-dialog persistent v-model="dialog" max-width="700px">
          <template v-slot:activator="{ on }">
            <v-btn color="primary" round outline v-on="on">
              <v-icon left>person</v-icon>
              {{ $t('validator.assign') }}
            </v-btn>
          </template>
          <v-card>
            <v-toolbar flat>
              <h2 class="font-weight-black primary--text mb-0">
                {{ formTitle }}
              </h2>
            </v-toolbar>
            <v-card-text>
              <v-container grid-list-xs>
                <v-form lazy-validation ref="form" v-model="valid">
                  <v-autocomplete
                    v-model="editedItem.user_id"
                    prepend-icon="person"
                    :disabled="this.editedIndex > -1"
                    :rules="rules.required"
                    :items="filterUsers"
                    item-text="name"
                    item-value="id"
                    :label="$t('validator.selectEmployee')"
                    :error-messages="errors.user_id"
                  >
                    <template slot="selection" slot-scope="data">
                      [{{
                        $t(
                          'role.' +
                            (data.item.roles ? data.item.roles[0].name : '')
                        )
                      }}] - {{ data.item.name }}
                    </template>
                    <template slot="item" slot-scope="data">
                      [{{
                        $t(
                          'role.' +
                            (data.item.roles ? data.item.roles[0].name : '')
                        )
                      }}] - {{ data.item.name }}
                    </template>
                  </v-autocomplete>
                  <v-autocomplete
                    v-model="editedItem.gst_area_id"
                    prepend-icon="group_work"
                    :rules="rules.required"
                    :items="filterAreas"
                    :item-disabled="isItemDisabled"
                    :multiple="editedIndex === -1 ? true : false"
                    @change="changeArea"
                    item-text="name"
                    item-value="id"
                    :label="editedIndex === -1 ? $t('validator.selectArea') + 's' : $t('validator.selectArea')"
                    :error-messages="errors.gst_area_id"
                  >
                    <template v-slot:selection="data" v-if="editedIndex === -1">
                      <v-chip :selected="data.selected" close @input="remove(data.item)">
                        <strong>{{ data.item.name }}</strong>&nbsp;
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </v-form>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary darken-1" round flat @click="close">
                {{ $t('general.cancel') }}
              </v-btn>
              <v-btn
                :loading="loading"
                :disabled="loading"
                color="primary"
                round
                depressed
                @click="save"
              >
                {{ $t('general.submit') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-layout>
      <v-data-table
        :search="validatorSearch"
        class="elevation-2"
        :headers="headers"
        :items="filterValidators"
        :loading="loadingItems"
        :rows-per-page-items="[10, 15, 25]"
        :pagination.sync="pagination"
      >
        <template v-slot:headers="props">
          <tr class="bg-heavy-rain">
            <th
              v-for="header in props.headers"
              :key="header.text"
              :class="[
                'font-weight-black white--text column',
                header.sortable ? 'sortable' : '',
                pagination.descending ? 'desc' : 'asc',
                header.value === pagination.sortBy ? 'active' : '',
              ]"
              @click="changeSort(header.value)"
            >
              <h2 class="font-weight-black primary--text mb-0">
                <v-icon small class="primary--text" v-if="header.sortable"
                  >arrow_upward</v-icon
                >
                {{ header.text }}
              </h2>
            </th>
          </tr>
        </template>
        <template v-slot:items="props">
          <tr>
            <td width="60px">{{ props.item.id }}</td>
            <td class="text-sm-center">{{ props.item.user.name }}</td>
            <td class="text-sm-center">{{ props.item.area.name }}</td>
            <td width="200px">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    small
                    outline
                    icon
                    color="info"
                    v-on="on"
                    @click="editItem(props.item)"
                  >
                    <v-icon small>edit</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('general.edit') }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    small
                    outline
                    icon
                    color="error"
                    v-on="on"
                    @click="removed(props.item)"
                  >
                    <v-icon small>delete</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('general.delete') }}</span>
              </v-tooltip>
            </td>
          </tr>
        </template>
      </v-data-table>
      <v-dialog persistent v-model="removeDialog" max-width="500px">
        <v-card>
          <v-toolbar flat>
            <h2 class="font-weight-black error--text mb-0">
              {{ $t('validator.delete') }}
            </h2>
          </v-toolbar>
          <v-card-text class="text-sm-justify">
            <p>{{ $t('general.deleteElementQuestion') }}</p>
            <p class="font-weight-black">
              <span class="error--text">{{ $t('general.name') }}:</span>{{ removedItem.user.name }}
            </p>
            <p class="font-weight-black">
              <span class="error--text">{{ $t('area.name') }}:</span>{{ removedItem.area.name }}
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="error darken-1"
              round
              flat
              @click="removeDialog = false"
            >
              {{ $t('general.cancel') }}
            </v-btn>
            <v-btn
              color="error"
              round
              depressed
              @click="removeItem(removedItem)"
            >
              {{ $t('general.delete') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  name: 'validatorsComponent',
  data() {
    return {
      validatorSearch: '',
      pagination: {
        descending: false,
        page: 0,
        rowsPerPage: 15, // -1 for All",
        totalItems: 0,
      },
      dialog: false,
      removeDialog: false,
      valid: true,
      editedIndex: -1,
      removedItem: {
        user: {
          name: '',
        },
        area: {
          name: '',
        },
      },
      originalItem: {},
      editedItem: {
        id: 0,
        user_id: 0,
        gst_area_id: [],
        status: 0,
      },
      defaultItem: {
        id: 0,
        user_id: 0,
        gst_area_id: [],
        status: 0,
      },
      userLogged: {
        roles: [
          {
            id: null,
          },
        ],
        company: [
          {
            id: null,
          },
        ],
      },
      users: [],
      validators: [],
      areas: [],
      errors: {},
      loading: false,
      loadingItems: true,
    }
  },
  computed: {
    heading() {
      return this.$t('general.validators') 
    },
    subheading() {
      return ''
    },
    icon() {
      return 'fas fa-user-check fa-fw icon-color'
    },
    headers() {
      return [
        {
          text: '#',
          align: 'center',
          value: 'id',
          sortable: true,
        },
        {
          text: this.$t('general.name'),
          align: 'center',
          value: 'user.name',
          sortable: true,
        },
        {
          text: this.$t('area.name'),
          align: 'center',
          value: 'area.name',
          sortable: true,
        },
        {
          text: this.$t('general.actions'),
          align: 'center',
          sortable: false,
        },
      ]
    },
    rules() {
      return this.$validationRules()
    },
    formTitle() {
      return this.editedIndex === -1 ? this.$t('validator.assign') : this.$t('validator.edit')
    },
    filterUsers() {
      return this.users.filter((item) => item.company.length !== 0 && item.company[0].id === this.userLogged.company[0].id && item.active === 1)
    },
    filterAreas() {
      let areFilter = this.areas.filter(
        (item) => item.company_id === this.userLogged.company[0].id
      )

      if(areFilter.length > 1 && this.editedIndex === -1) {
        let alls = { id: -1, name: this.$t('general.all') }
        areFilter.unshift(alls)
      }
      return areFilter
      
    },
    filterValidators() {
      return this.validators.filter(
        (item) => item.area.company_id === this.userLogged.company[0].id
      )
    },
  },
  watch: {
    dialog(val) {
      val || this.close()
    },
    '$store.state.userLogged'() {
      this.getLogged()
    },
  },
  created() {
    this.getLogged()
    this.initialize()
  },
  methods: {
    isItemDisabled(item) {
      if (this.editedIndex !== -1) { return false }

      const isTodoSelected = this.editedItem.gst_area_id.includes(-1)
      
      return isTodoSelected && item.id !== -1;
    },
    changeArea() {
      if (this.editedItem.gst_area_id.length > 0 && this.editedItem.gst_area_id.includes(-1)) {
        this.editedItem.gst_area_id = [-1]
      }
    },
    getLogged() {
      if (this.$store.state.userLogged.company) {
        this.userLogged = this.$store.state.userLogged
      }
    },
    changeSort(header) {
      if (this.pagination.sortBy === header) {
        this.pagination.descending = !this.pagination.descending
      } else {
        this.pagination.descending = false
      }
      this.pagination.sortBy = header
    },
    remove(item) {
      if (this.editedItem.gst_area_id.length > 0) {
        let areas = this.editedItem.gst_area_id
        areas.splice(areas.indexOf(item.id), 1)
        areas = [...areas]
      }
    },
    removed(item) {
      // set the item that want to remove and open the dialog
      this.removedItem = item
      this.removeDialog = true
    },
    removeItem(item) {
      // change the status of the users to '0'
      const index = this.validators.indexOf(item)
      this.delete(item, index)
      this.removeDialog = false
    },
    close() {
      // close the dialog and re-assing default values
      this.dialog = false
      this.editedItem = Object.assign({}, this.defaultItem)
      this.editedIndex = -1
      this.$refs.form.resetValidation()
      this.errors = {}
    },
    editItem(item) {
      // show the element that want to edit
      this.editedIndex = this.validators.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.originalItem = Object.assign({}, item)
      this.dialog = true
    },
    save() {
      // evaluate the form and save de values
      if (this.$refs.form.validate()) {
        this.loading = true
        if (this.editedIndex > -1) {
          this.put(this.editedItem, this.editedIndex)
        } else {
          this.post()
        }
      }
    },
    initialize() {
      this.$emit('usersGet', { context: this })
      this.$emit('areasGet', { context: this })
      this.$emit('validatorsGet', { context: this })
    },
    post() {
      let areas = []

      if(this.editedItem.gst_area_id.includes(-1)) {
        this.filterAreas.forEach((area) => {
           if(area.id !== -1)areas.push({ area: area.id })
        })
      } else {
        this.editedItem.gst_area_id.forEach((area) => {
          areas.push({ area: area })
        })
      }
      
      this.$emit('validatorsPost', {
        item: this.editedItem,
        areas: areas,
        context: this,
      })
    },
    put(item, index) {
      this.$emit('validatorsPut', { item: item, index: index, context: this })
    },
    delete(item, index) {
      this.$emit('validatorsDelete', {
        item: item,
        index: index,
        context: this,
      })
    },
    refresh() {
      this.$emit('validatorsGet', { context: this })
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/validators.component.vue